import { isAlive, isStateTreeNode } from 'mobx-state-tree';
import type { IMetaModel } from '../../stores/model/metamodel';
import type { IObjectType } from '../../stores/model/metamodel/object_type';
import type { IEventType } from '../../stores/model/metamodel/event_type';
import type { IDependency } from '../../stores/model/metamodel/dependency';
import type { IMethod } from '../../stores/model/metamodel/method';
import type { IInheritance } from '../../stores/model/metamodel/inheritance';
import type { IDataType } from '../../stores/model/metamodel/data_type';
import type { IFSM } from '../../stores/model/metamodel/fsm';
import type { IState } from '../../stores/model/metamodel/state';
import type { ITransition } from '../../stores/model/metamodel/transition';
import type { IAttribute } from '../../stores/model/metamodel/attribute';
import type { IMultiplePropagationConstraint } from '../../stores/model/metamodel/multiple_propagation_constraint';
import type Report from './Report';

/**
 * Any element a check can run against or report on. This is the metamodel
 * itself (for the model-wide checks) plus every metamodel element a check
 * targets or names as the subject of a message.
 */
export type CheckSubject =
  | IMetaModel
  | IObjectType
  | IEventType
  | IDependency
  | IMethod
  | IInheritance
  | IDataType
  | IFSM
  | IState
  | ITransition
  | IAttribute
  | IMultiplePropagationConstraint;

/**
 * Shape of a Check subclass constructor, used to read the static `description`
 * off `this.constructor`.
 */
interface CheckConstructor {
  description: string;
}

/**
 * Base class for checks.
 *
 * `TElement` is the type of the element under test. It defaults to the
 * metamodel for the model-wide checks (dependency/inheritance loops) that have
 * no specific element.
 */
class Check<TElement extends CheckSubject = IMetaModel> {
  static description = 'Generic check';

  model: IMetaModel;
  element: TElement;
  report?: Report;

  constructor(model: IMetaModel, element: TElement | null = null) {
    this.model = model;
    // When no element is given, the check targets the whole metamodel. The
    // default-element subclasses (loop checks) leave `TElement` as `IMetaModel`,
    // so the fallback is sound; specialised subclasses always pass an element.
    this.element =
      element !== null ? element : (model as unknown as TElement);
  }

  /**
   * Get the description of the check.
   */
  get description(): string {
    return (this.constructor as unknown as CheckConstructor).description;
  }

  /**
   * Is the check obsolete?
   *
   * A check is obsolete when its element has been removed. A check for a dead element can be added
   * to the list of checks during cascading deletes that first modify an element and end up removing
   * it later.
   */
  get obsolete(): boolean {
    return this.element != null && isStateTreeNode(this.element) && !isAlive(this.element);
  }

  /**
   * Get the name of the check.
   *
   * (aka the class name/constructor name of the check)
   */
  get name(): string {
    return this.constructor.name;
  }
}

export default Check;
